import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { withLocale } from "~/lib/utils/with-locale";
import { getLocation } from "~/lib/server-functions/get-locations";
import { transformLocationDataToForm } from "~/lib/add-location/transform-location-data";
import { LocationForm } from "~/lib/add-location/components/LocationForm";
import { InsertLocationSchema } from "~/lib/add-location/types";
import { useUpdateLocation } from "~/lib/tanstack-query/hooks/useUpdateLocation";
import { checkLocationOwnership } from "~/lib/server-functions/check-location-ownership";
import { getCmsPageContent } from "~/lib/server-functions/get-cms-page-content";
import { ContentRenderer } from "~/components/ContentRenderer";
import z from "zod";

export const Route = createFileRoute(
  "/advertiser/_assistant/edit-location/$id",
)({
  component: RouteComponent,
  loader: async ({ params, location: routerLocation }) => {
    const id = parseInt(params.id, 10);

    // Verify user owns this location
    const ownsLocation = await checkLocationOwnership({ data: id });

    if (!ownsLocation) {
      throw new Error("Unauthorized: You do not own this location");
    }

    const location = await getLocation({ data: id });
    if (!location) {
      throw new Error("Location not found");
    }

    const transformed = transformLocationDataToForm(location);

    let cmsContent: Awaited<ReturnType<typeof getCmsPageContent>>["content"] =
      [];
    try {
      const result = await getCmsPageContent({
        data: { slug: "/edit-location" },
      });
      if (result.content?.length) cmsContent = result.content;
    } catch {
      // No CMS content for this slug; show form only
    }

    return {
      location,
      locationId: id,
      initialFormData: transformed,
      cmsContent,
    };
  },
});

function RouteComponent() {
  const { location, locationId, initialFormData, cmsContent } =
    Route.useLoaderData();
  const navigate = useNavigate();
  const { mutateAsync: updateLocation } = useUpdateLocation();

  const handleSubmit = async (values: z.infer<typeof InsertLocationSchema>) => {
    // Actually call the update function
    await updateLocation({ ...values, id: locationId });
    // Navigate after successful update
    navigate({ to: withLocale("/locations") });
  };

  return (
    <>
      <LocationForm
        initialData={initialFormData as z.infer<typeof InsertLocationSchema>}
        mode="edit"
        locationId={locationId}
        locationStatus={location?.status ?? undefined}
        planOrdered={location?.planOrdered ?? false}
        onSubmit={handleSubmit}
      />
      {cmsContent.length > 0 && (
        <div className="mt-8">
          {cmsContent.map((item: { uid: number }) => (
            <ContentRenderer key={item.uid} item={item} />
          ))}
        </div>
      )}
    </>
  );
}
